import React, { useEffect, useState, useMemo } from "react";
import { Box, Button, CircularProgress, Switch } from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../../../shared/hooks/sharedHooks";
import useTranslation from "../../../../shared/hooks/useTranslation";
import ActionToast from "../../../../shared/components/ActionToast";
import SettingsCard from "../shared/SettingsCard";
import { dt, accentGradientDark, accentGradientDarkHover } from "../../../../shared/themes/redesignTokens";
import {
  getPricingDefaults,
  savePricingDefaults,
  clearPricingSuccess,
  clearPricingError,
} from "../../serviceProviderPricingSlice";


const DAYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"] as const;

const SettingsService: React.FC = () => {
  const { translate } = useTranslation("settings");
  const { translate: tCommon } = useTranslation("common");
  const dispatch = useAppDispatch();
  const { settings, loading, saving, success, error } = useAppSelector(
    (state) => state.serviceProviderPricing
  );

  // Local state - Service Radius
  const [radius, setRadius] = useState("");
  const [loadedRadius, setLoadedRadius] = useState("");

  // Local state - Business Hours
  const [deadlineEnabled, setDeadlineEnabled] = useState(false);
  const [deadlineTime, setDeadlineTime] = useState("16:00");
  const [usePerDay, setUsePerDay] = useState(false);
  const [perDayTimes, setPerDayTimes] = useState<Record<string, string>>({
    monday: "16:00",
    tuesday: "16:00",
    wednesday: "16:00",
    thursday: "16:00",
    friday: "16:00",
    saturday: "",
    sunday: "",
  });

  // Track loaded values for dirty detection
  const [loadedDeadlineEnabled, setLoadedDeadlineEnabled] = useState(false);
  const [loadedDeadlineTime, setLoadedDeadlineTime] = useState("16:00");
  const [loadedUsePerDay, setLoadedUsePerDay] = useState(false);
  const [loadedPerDayTimes, setLoadedPerDayTimes] = useState<Record<string, string>>({});

  // Auto-detected timezone
  const detectedTimezone = useMemo(() => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch {
      return "UTC";
    }
  }, []);

  // Fetch settings on mount
  useEffect(() => {
    dispatch(getPricingDefaults());
  }, [dispatch]);

  // Helper to format TimeSpan string (e.g. "16:00:00") to "HH:mm"
  const formatTimeSpan = (ts: string | null | undefined): string => {
    if (!ts) return "";
    // TimeSpan from .NET can be "16:00:00" or "1.16:00:00" (with days)
    const parts = ts.split(":");
    if (parts.length >= 2) {
      // Handle day prefix like "1.16"
      const hourPart = parts[0].includes(".") ? parts[0].split(".")[1] : parts[0];
      return `${hourPart.padStart(2, "0")}:${parts[1].padStart(2, "0")}`;
    }
    return "";
  };

  // Populate form when settings arrive
  useEffect(() => {
    if (settings) {
      const r = settings.serviceRadiusKm != null ? String(settings.serviceRadiusKm) : "150";
      setRadius(r);
      setLoadedRadius(r);

      // Business hours fields
      const de = settings.orderDeadlineEnabled ?? false;
      setDeadlineEnabled(de);
      setLoadedDeadlineEnabled(de);

      const dt2 = formatTimeSpan(settings.orderDeadlineTime) || "16:00";
      setDeadlineTime(dt2);
      setLoadedDeadlineTime(dt2);

      const upd = settings.usePerDayDeadlines ?? false;
      setUsePerDay(upd);
      setLoadedUsePerDay(upd);

      const pdt: Record<string, string> = {
        monday: formatTimeSpan(settings.mondayDeadlineTime) || "16:00",
        tuesday: formatTimeSpan(settings.tuesdayDeadlineTime) || "16:00",
        wednesday: formatTimeSpan(settings.wednesdayDeadlineTime) || "16:00",
        thursday: formatTimeSpan(settings.thursdayDeadlineTime) || "16:00",
        friday: formatTimeSpan(settings.fridayDeadlineTime) || "16:00",
        saturday: formatTimeSpan(settings.saturdayDeadlineTime) || "",
        sunday: formatTimeSpan(settings.sundayDeadlineTime) || "",
      };
      setPerDayTimes(pdt);
      setLoadedPerDayTimes({ ...pdt });
    }
  }, [settings]);

  const isDirty = useMemo(() => {
    if (loadedRadius !== "" && radius !== loadedRadius) return true;
    if (deadlineEnabled !== loadedDeadlineEnabled) return true;
    if (deadlineEnabled) {
      if (deadlineTime !== loadedDeadlineTime) return true;
      if (usePerDay !== loadedUsePerDay) return true;
      if (usePerDay && JSON.stringify(perDayTimes) !== JSON.stringify(loadedPerDayTimes)) return true;
    }
    return false;
  }, [
    radius, loadedRadius, deadlineEnabled, loadedDeadlineEnabled,
    deadlineTime, loadedDeadlineTime, usePerDay, loadedUsePerDay,
    perDayTimes, loadedPerDayTimes,
  ]);

  // Helper to convert "HH:mm" to "HH:mm:00" TimeSpan format for .NET
  const toTimeSpan = (time: string): string | null => {
    if (!time) return null;
    return `${time}:00`;
  };

  const handleSave = () => {
    dispatch(
      savePricingDefaults({
        serviceRadiusKm: radius ? Number(radius) : null,
        baseLatitude: settings?.baseLatitude ?? null,
        baseLongitude: settings?.baseLongitude ?? null,
        hourlyRate: settings?.hourlyRate ?? null,
        travelRatePerKm: settings?.travelRatePerKm ?? null,
        travelRatePerHour: settings?.travelRatePerHour ?? null,
        warrantyPeriodCode: settings?.warrantyPeriodCode ?? null,
        customWarrantyPeriod: settings?.customWarrantyPeriod ?? null,
        paymentTermsCode: settings?.paymentTermsCode ?? null,
        customPaymentTerms: settings?.customPaymentTerms ?? null,
        validityPeriodCode: settings?.validityPeriodCode ?? null,
        customValidityPeriod: settings?.customValidityPeriod ?? null,
        includeDiagnostics: settings?.includeDiagnostics ?? false,
        includeLaborForRepair: settings?.includeLaborForRepair ?? false,
        includePartsAndMaterials: settings?.includePartsAndMaterials ?? false,
        includeTestingAndCalibration: settings?.includeTestingAndCalibration ?? false,
        includeWorkAreaCleanup: settings?.includeWorkAreaCleanup ?? false,
        includeDetailedServiceReport: settings?.includeDetailedServiceReport ?? false,
        // Business hours fields
        orderDeadlineEnabled: deadlineEnabled,
        orderDeadlineTime: deadlineEnabled ? toTimeSpan(deadlineTime) : null,
        orderDeadlineTimezoneId: deadlineEnabled ? detectedTimezone : null,
        usePerDayDeadlines: deadlineEnabled ? usePerDay : false,
        mondayDeadlineTime: deadlineEnabled && usePerDay ? toTimeSpan(perDayTimes.monday) : null,
        tuesdayDeadlineTime: deadlineEnabled && usePerDay ? toTimeSpan(perDayTimes.tuesday) : null,
        wednesdayDeadlineTime: deadlineEnabled && usePerDay ? toTimeSpan(perDayTimes.wednesday) : null,
        thursdayDeadlineTime: deadlineEnabled && usePerDay ? toTimeSpan(perDayTimes.thursday) : null,
        fridayDeadlineTime: deadlineEnabled && usePerDay ? toTimeSpan(perDayTimes.friday) : null,
        saturdayDeadlineTime: deadlineEnabled && usePerDay ? toTimeSpan(perDayTimes.saturday) : null,
        sundayDeadlineTime: deadlineEnabled && usePerDay ? toTimeSpan(perDayTimes.sunday) : null,
      })
    ).then((result) => {
      if (savePricingDefaults.fulfilled.match(result)) {
        setLoadedRadius(radius);
        setLoadedDeadlineEnabled(deadlineEnabled);
        setLoadedDeadlineTime(deadlineTime);
        setLoadedUsePerDay(usePerDay);
        setLoadedPerDayTimes({ ...perDayTimes });
      }
    });
  };

  const handlePerDayTimeChange = (day: string, value: string) => {
    setPerDayTimes((prev) => ({ ...prev, [day]: value }));
  };

  const saveButton = (
    <Button
      variant="contained"
      onClick={handleSave}
      disabled={!isDirty || saving}
      sx={{
        background: isDirty
          ? accentGradientDark
          : "#E5E7EB",
        color: isDirty ? "#fff" : dt.textMuted,
        borderRadius: dt.radiusPill,
        padding: "8px 24px",
        textTransform: "none",
        fontWeight: 600,
        fontSize: "13px",
        fontFamily: dt.font,
        boxShadow: isDirty ? "0 2px 8px rgba(52,152,219,0.25)" : "none",
        transition: "all 0.3s ease",
        whiteSpace: "nowrap",
        "&:hover": {
          background: isDirty
            ? accentGradientDarkHover
            : "#E5E7EB",
          transform: isDirty ? "translateY(-1px)" : "none",
          boxShadow: isDirty ? "0 4px 12px rgba(52,152,219,0.3)" : "none",
        },
        "&.Mui-disabled": {
          background: "#E5E7EB",
          color: dt.textMuted,
        },
      }}
    >
      {saving ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <CircularProgress size={14} sx={{ color: "#fff" }} />
          {translate("settings:service.saving")}
        </Box>
      ) : (
        tCommon("buttons.save")
      )}
    </Button>
  );

  const sectionLabelSx = {
    fontSize: "11px",
    fontWeight: 700,
    textTransform: "uppercase" as const,
    letterSpacing: "1.5px",
    color: dt.textMuted,
    fontFamily: dt.font,
    mb: 1,
  };

  const sectionDescSx = {
    fontSize: "13px",
    color: dt.textSecondary,
    fontFamily: dt.font,
    mb: 2,
    lineHeight: 1.5,
  };

  const inputSx = {
    width: "120px",
    padding: "10px 14px",
    border: "1.5px solid #E5E7EB",
    borderRadius: dt.radiusSm,
    backgroundColor: dt.inputBg,
    fontFamily: dt.fontMono,
    fontSize: "16px",
    fontWeight: 600,
    color: dt.textPrimary,
    outline: "none",
    boxSizing: "border-box" as const,
    textAlign: "center" as const,
    transition: "all 0.2s ease",
    "&:focus": {
      borderColor: dt.blue,
      boxShadow: `0 0 0 3px ${dt.blueGlow}`,
    },
  };

  const timeInputSx = {
    ...inputSx,
    width: "110px",
    fontSize: "14px",
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "20px" }}>
      <ActionToast
        open={!!success}
        variant="success"
        message={translate("settings:service.saveSuccess")}
        onClose={() => dispatch(clearPricingSuccess())}
        duration={6000}
      />
      <ActionToast
        open={!!error}
        variant="error"
        message={translate("settings:service.saveError")}
        onClose={() => dispatch(clearPricingError())}
        duration={6000}
      />

      {/* Service Radius Card */}
      <SettingsCard
        title={translate("settings:service.title")}
        action={saveButton}
        animationDelay="0.15s"
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: "20px" }}>
          {/* Service Radius section */}
          <Box>
            <Box sx={sectionLabelSx}>
              {translate("settings:service.radiusLabel")}
            </Box>
            <Box sx={sectionDescSx}>
              {translate("settings:service.radiusDescription")}
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <Box
                component="input"
                type="number"
                value={radius}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setRadius(e.target.value)}
                min="0"
                max="5000"
                sx={inputSx}
              />
              <Box
                sx={{
                  fontSize: "14px",
                  fontWeight: 600,
                  color: dt.textSecondary,
                  fontFamily: dt.font,
                }}
              >
                {translate("settings:service.radiusUnit")}
              </Box>
            </Box>
          </Box>

          {/* Categories placeholder */}
          <Box sx={{ borderTop: "1px solid #F3F4F6", pt: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
              <Box sx={{ fontSize: "14px", color: dt.textSecondary, fontFamily: dt.font }}>
                {translate("settings:service.categories")}
              </Box>
              <Box
                sx={{
                  fontSize: "11px",
                  fontWeight: 600,
                  color: dt.textMuted,
                  fontFamily: dt.font,
                  backgroundColor: "#F4F6F9",
                  padding: "3px 10px",
                  borderRadius: "10px",
                }}
              >
                Coming soon
              </Box>
            </Box>
          </Box>
        </Box>
      </SettingsCard>

      {/* Business Hours Card */}
      <SettingsCard
        title={translate("settings:service.businessHoursTitle")}
        animationDelay="0.25s"
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: "20px" }}>
          {/* Enable deadline toggle */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box>
              <Box sx={{ fontSize: "14px", fontWeight: 500, color: dt.textPrimary, fontFamily: dt.font }}>
                {translate("settings:service.enableDeadline")}
              </Box>
              <Box sx={{ fontSize: "12px", color: dt.textSecondary, fontFamily: dt.font, mt: 0.3 }}>
                {translate("settings:service.enableDeadlineDescription")}
              </Box>
            </Box>
            <Switch
              checked={deadlineEnabled}
              onChange={(e) => setDeadlineEnabled(e.target.checked)}
              sx={{
                "& .MuiSwitch-switchBase.Mui-checked": {
                  color: dt.blue,
                },
                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                  backgroundColor: dt.blue,
                },
              }}
            />
          </Box>

          {deadlineEnabled && (
            <>
              {/* Single deadline time */}
              <Box>
                <Box sx={sectionLabelSx}>
                  {translate("settings:service.acceptOrdersUntil")}
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <Box
                    component="input"
                    type="time"
                    value={deadlineTime}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setDeadlineTime(e.target.value)
                    }
                    sx={timeInputSx}
                  />
                </Box>
              </Box>

              {/* Per-day toggle */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  borderTop: "1px solid #F3F4F6",
                  pt: 2,
                }}
              >
                <Box>
                  <Box sx={{ fontSize: "14px", fontWeight: 500, color: dt.textPrimary, fontFamily: dt.font }}>
                    {translate("settings:service.usePerDayTimes")}
                  </Box>
                  <Box sx={{ fontSize: "12px", color: dt.textSecondary, fontFamily: dt.font, mt: 0.3 }}>
                    {translate("settings:service.usePerDayTimesDescription")}
                  </Box>
                </Box>
                <Switch
                  checked={usePerDay}
                  onChange={(e) => setUsePerDay(e.target.checked)}
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": {
                      color: dt.blue,
                    },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                      backgroundColor: dt.blue,
                    },
                  }}
                />
              </Box>

              {/* Per-day time inputs */}
              {usePerDay && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                  {DAYS.map((day) => (
                    <Box
                      key={day}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        padding: "8px 0",
                        borderBottom: "1px solid #F3F4F6",
                      }}
                    >
                      <Box
                        sx={{
                          fontSize: "14px",
                          fontWeight: 500,
                          color: dt.textPrimary,
                          fontFamily: dt.font,
                          textTransform: "capitalize",
                          minWidth: "100px",
                        }}
                      >
                        {translate(`settings:service.days.${day}`)}
                      </Box>
                      <Box
                        component="input"
                        type="time"
                        value={perDayTimes[day] || ""}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          handlePerDayTimeChange(day, e.target.value)
                        }
                        sx={timeInputSx}
                      />
                    </Box>
                  ))}
                </Box>
              )}

              {/* Timezone display */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  padding: "10px 14px",
                  backgroundColor: "#F4F6F9",
                  borderRadius: dt.radiusSm,
                }}
              >
                <Box
                  sx={{
                    fontSize: "12px",
                    color: dt.textSecondary,
                    fontFamily: dt.font,
                  }}
                >
                  {translate("settings:service.timezone")}:
                </Box>
                <Box
                  sx={{
                    fontSize: "13px",
                    fontWeight: 600,
                    color: dt.textPrimary,
                    fontFamily: dt.fontMono,
                  }}
                >
                  {detectedTimezone}
                </Box>
              </Box>
            </>
          )}
        </Box>
      </SettingsCard>
    </Box>
  );
};

export default SettingsService;
